import React, {
  CSSProperties,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useSupportsHover } from 'hooks/useSupportsHover';

const SCROLL_AMOUNT = 500;

const HorizontalScrollContainer = ({ children }: PropsWithChildren) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const supportsHover = useSupportsHover();

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkScroll, { passive: true });
    const observer = new ResizeObserver(checkScroll);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      observer.disconnect();
    };
  }, [checkScroll]);

  const scroll = (direction: 'left' | 'right') => {
    scrollRef.current?.scrollBy({
      left: direction === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT,
      behavior: 'smooth'
    });
  };

  return (
    <div
      style={containerStyle}
      // Hover-driven scroll arrows are desktop-only — on iOS the first tap
      // fires mouseenter (touch emulation), which both flips the hover state
      // *and* suppresses the click that would have navigated. Skipping the
      // handlers on touch devices lets the first tap on a child card go
      // straight through to its onClick.
      onMouseEnter={supportsHover ? () => setIsHovered(true) : undefined}
      onMouseLeave={supportsHover ? () => setIsHovered(false) : undefined}
    >
      {/* Left arrow */}
      {canScrollLeft && isHovered && (
        <button style={{ ...arrowStyle, left: 0 }} onClick={() => scroll('left')}>
          <ChevronLeftIcon style={{ fontSize: '1.5rem' }} />
        </button>
      )}

      {/* Scrollable content */}
      <div ref={scrollRef} style={scrollStyle}>
        {children}
      </div>

      {/* Right arrow */}
      {canScrollRight && isHovered && (
        <button style={{ ...arrowStyle, right: 0 }} onClick={() => scroll('right')}>
          <ChevronRightIcon style={{ fontSize: '1.5rem' }} />
        </button>
      )}
    </div>
  );
};

export default HorizontalScrollContainer;

const containerStyle: CSSProperties = {
  position: 'relative',
  width: '100%'
};

const scrollStyle: CSSProperties = {
  display: 'flex',
  gap: '8px',
  overflowX: 'auto',
  scrollBehavior: 'smooth',
  padding: '8px 0',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  WebkitOverflowScrolling: 'touch'
} as CSSProperties;

const arrowStyle: CSSProperties = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 10,
  width: 36,
  height: 36,
  borderRadius: '50%',
  border: 'none',
  background: 'rgba(0, 0, 0, 0.7)',
  backdropFilter: 'blur(4px)',
  color: 'white',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s ease'
};
